/**
 * Constantes liées aux devis (quotes)
 * Source de vérité unique — miroir du schéma Prisma
 */

/**
 * Intro par défaut de l'email d'envoi de devis, pré-remplie dans le champ
 * « Personnaliser l'email » de l'aperçu PDF.
 * ⚠️ Doit rester synchronisée avec DEFAULT_QUOTE_SENT_INTRO côté backend
 * (server/utils/email/quote-emails-status.js).
 */
export const DEFAULT_QUOTE_EMAIL_INTRO = 'Bonjour,\n\nSuite à votre demande, nous avons le plaisir de vous transmettre votre devis personnalisé.'

export const QuoteStatus = {
  DEMANDE_DEVIS: 'demande_devis',
  APPEL_QUALIFICATION: 'appel_qualification',
  DEVIS_EN_ATTENTE: 'devis_en_attente',
  DEVIS_SIGNE: 'devis_signe',
  DEMANDE_PERDUE: 'demande_perdue',
  DEVIS_PERDU: 'devis_perdu',
  ENCODE_DIGIFORMA: 'encode_digiforma',
  CORBEILLE: 'corbeille',
}

/**
 * Statut greffé retiré du process devis mais pouvant subsister en base.
 * Là où on résout libellé/badge, on le traite comme DEVIS_EN_ATTENTE
 * (fallback d'affichage — ne pas réintroduire la greffe).
 */
const LEGACY_PROPOSITION_SESSIONS_ENVOYEES = 'proposition_sessions_envoyees'

export const QUOTE_STATUS_LABELS = {
  [QuoteStatus.DEMANDE_DEVIS]: 'Demande de devis',
  [QuoteStatus.APPEL_QUALIFICATION]: 'Appel de qualification / Confirmation des infos',
  [QuoteStatus.DEVIS_EN_ATTENTE]: 'Devis envoyé – en attente de signature',
  [QuoteStatus.DEVIS_SIGNE]: 'Devis signé (gagné - à encoder sur digiforma)',
  [QuoteStatus.DEMANDE_PERDUE]: 'Demande perdue',
  [QuoteStatus.DEVIS_PERDU]: 'Devis perdu',
  [QuoteStatus.ENCODE_DIGIFORMA]: 'Encodé dans Digiforma (action réalisée)',
  [QuoteStatus.CORBEILLE]: 'Corbeille',
}

export const QUOTE_STATUS_OPTIONS = Object.entries(QUOTE_STATUS_LABELS)
  .filter(([value]) => value !== QuoteStatus.CORBEILLE)
  .map(([value, label]) => ({ value, label }))

export const ACTIVE_QUOTE_STATUSES = [
  QuoteStatus.DEMANDE_DEVIS,
  QuoteStatus.APPEL_QUALIFICATION,
  QuoteStatus.DEVIS_EN_ATTENTE,
  QuoteStatus.DEVIS_SIGNE,
]

export const HISTORY_QUOTE_STATUSES = [
  QuoteStatus.DEMANDE_PERDUE,
  QuoteStatus.DEVIS_PERDU,
  QuoteStatus.ENCODE_DIGIFORMA,
  QuoteStatus.CORBEILLE,
]

export const ACTIVE_QUOTE_STATUS_OPTIONS = ACTIVE_QUOTE_STATUSES.map(v => ({ value: v, label: QUOTE_STATUS_LABELS[v] }))
export const HISTORY_QUOTE_STATUS_OPTIONS = HISTORY_QUOTE_STATUSES.map(v => ({ value: v, label: QUOTE_STATUS_LABELS[v] }))

export const QUOTE_STATUS_SHORT_LABELS = {
  null: 'Création',
  [QuoteStatus.DEMANDE_DEVIS]: 'Demande devis',
  [QuoteStatus.APPEL_QUALIFICATION]: 'Appel qualif.',
  [QuoteStatus.DEVIS_EN_ATTENTE]: 'Devis attente',
  [QuoteStatus.DEVIS_SIGNE]: 'Devis signé',
  [QuoteStatus.DEMANDE_PERDUE]: 'Demande perdue',
  [QuoteStatus.DEVIS_PERDU]: 'Devis perdu',
  [QuoteStatus.ENCODE_DIGIFORMA]: 'Encodé Digiforma',
}

/**
 * Libellés intermédiaires (canvas de refonte de la liste) : plus lisibles que
 * les courts abrégés, plus compacts que les intitulés complets qui débordent
 * d'un sélecteur de colonne.
 */
export const QUOTE_STATUS_MEDIUM_LABELS = {
  [QuoteStatus.DEMANDE_DEVIS]: 'Demande de devis',
  [QuoteStatus.APPEL_QUALIFICATION]: 'Appel de qualification',
  [QuoteStatus.DEVIS_EN_ATTENTE]: 'Devis en attente de signature',
  [QuoteStatus.DEVIS_SIGNE]: 'Devis signé',
  [QuoteStatus.DEMANDE_PERDUE]: 'Demande perdue',
  [QuoteStatus.DEVIS_PERDU]: 'Devis perdu',
  [QuoteStatus.ENCODE_DIGIFORMA]: 'Encodé dans Digiforma',
  [QuoteStatus.CORBEILLE]: 'Corbeille',
}

export const QUOTE_STATUS_TRANSITION_ORDER = [
  'null',
  QuoteStatus.DEMANDE_DEVIS,
  QuoteStatus.APPEL_QUALIFICATION,
  QuoteStatus.DEVIS_EN_ATTENTE,
  QuoteStatus.DEVIS_SIGNE,
  QuoteStatus.DEMANDE_PERDUE,
  QuoteStatus.DEVIS_PERDU,
  QuoteStatus.ENCODE_DIGIFORMA,
]

export const QUOTE_STATUS_TRANSITION_OPTIONS = QUOTE_STATUS_OPTIONS.map(({ value, label }) => {
  const short = QUOTE_STATUS_SHORT_LABELS[value]
  return { value, label: short || label }
})

/**
 * Normalise un statut éventuellement legacy (greffe retirée).
 * `proposition_sessions_envoyees` est traité comme `devis_en_attente`.
 */
export function normalizeQuoteStatus(status) {
  if (status === LEGACY_PROPOSITION_SESSIONS_ENVOYEES) {
    return QuoteStatus.DEVIS_EN_ATTENTE
  }
  return status
}

export function formatQuoteStatus(status) {
  const normalized = normalizeQuoteStatus(status)
  if (!normalized) return ''
  return QUOTE_STATUS_LABELS[normalized] || normalized
}

export function formatQuoteStatusShort(status) {
  const normalized = normalizeQuoteStatus(status)
  return QUOTE_STATUS_SHORT_LABELS[normalized] || normalized || 'Création'
}

export function isValidQuoteStatus(status) {
  return status && Object.prototype.hasOwnProperty.call(QUOTE_STATUS_LABELS, status)
}

/**
 * Distingue "Demande perdue" de "Devis perdu" à partir du statut réel
 * (deux statuts distincts en base). Le statut choisi fait foi — aucune
 * dérivation depuis `quoteSentDate`.
 * @returns {'demande'|'devis'|null}
 */
export function getLossKind(quote) {
  if (!quote) return null
  if (quote.status === QuoteStatus.DEMANDE_PERDUE) return 'demande'
  if (quote.status === QuoteStatus.DEVIS_PERDU) return 'devis'
  return null
}

export function getLossLabel(quote) {
  const kind = getLossKind(quote)
  if (kind === 'demande') return 'Demande perdue'
  if (kind === 'devis') return 'Devis perdu'
  return ''
}
